import type { ShapeVertex } from '@repo/api-contracts';
import { Plus } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import type { HoleDrawTool } from '@/entities/edit-area';
import { Button, DeleteButton } from '@/shared/ui';
import { DrawToolBar } from './draw-tool-bar';

/** 인쇄 제외 영역 도구 줄 상태(인스펙터가 고른 영역·면·잠금으로 계산한다). */
export interface HoleToolControl {
  /** 지금 그리는 중인 도구. 그리지 않으면 null. */
  readonly pressed: HoleDrawTool | null;
  /** 사각형·원형을 쓸 수 없는 사유. 쓸 수 있으면 null. */
  readonly rectReason: string | null;
  /** 자유 다각형을 쓸 수 없는 사유. 쓸 수 있으면 null. */
  readonly polygonReason: string | null;
  /** 도구를 누른다 — 꺼져 있으면 그 도구로 구멍 하나 그리기를 시작하고, 이미 눌린 도구면 그리기를 끝낸다. */
  readonly onChoose: (tool: HoleDrawTool) => void;
  /** 그리는 중인 구멍을 그만 그린다([+]로 도구 줄을 닫을 때). */
  readonly onCancel: () => void;
}

interface ShapeHoleListProps {
  readonly holes: readonly (readonly ShapeVertex[])[];
  /** 사진 위·목록에서 고른 구멍 순번. */
  readonly selectedIndex: number | null;
  readonly onSelect: (index: number) => void;
  readonly onRemove: (index: number) => void;
  readonly disabled: boolean;
  readonly tools: HoleToolControl;
}

/**
 * 인쇄 영역의 인쇄 제외 영역 — 제목 오른쪽 작은 [+]를 누르면 제목 아래에 도구 줄(사각형·원형·자유 다각형)이 열린다.
 * 도구를 고르면 구멍 하나만 그리고, 더하거나 취소하면(그리기가 끝나면) 도구 줄이 닫힌다. 그 아래 목록 줄을 누르면 사진 위에서
 * 그 구멍을 고른다.
 */
export const ShapeHoleList = ({
  holes,
  selectedIndex,
  onSelect,
  onRemove,
  disabled,
  tools,
}: ShapeHoleListProps): ReactElement => {
  // [+]로 연 도구 줄. 도구를 고르면 닫힌 것으로 두고, 그리는 동안은 눌린 도구가 있어 줄이 보인다 — 그리기가 끝나면 닫힌다.
  const [open, setOpen] = useState(false);
  const drawing = tools.pressed !== null;
  const rowShown = open || drawing;
  // 두 도구를 다 쓸 수 없으면 [+]를 끈다(잠금·이 면 배치·최대 개수·원통 사각형/원형 영역). 사유는 자유 다각형 쪽이 더 넓다.
  const addReason =
    tools.rectReason !== null && tools.polygonReason !== null ? tools.polygonReason : null;
  const addDisabled = addReason !== null && !drawing;
  const toggle = (): void => {
    if (rowShown) {
      setOpen(false);
      if (drawing) tools.onCancel();
      return;
    }
    setOpen(true);
  };
  const choose = (tool: HoleDrawTool): void => {
    setOpen(false);
    tools.onChoose(tool);
  };
  return (
    <fieldset aria-label="인쇄 제외 영역" className="m-0 min-w-0 space-y-2 border-0 p-0">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">인쇄 제외 영역</h3>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="인쇄 제외 영역 추가"
          aria-expanded={rowShown}
          disabled={addDisabled}
          title={addReason ?? undefined}
          onClick={toggle}
        >
          <Plus aria-hidden />
        </Button>
      </div>
      {addDisabled && addReason !== null ? (
        <p className="text-sm text-muted-foreground">{addReason}</p>
      ) : null}
      {rowShown ? (
        <DrawToolBar
          variant="hole"
          pressedTool={tools.pressed}
          rectDisabled={
            tools.rectReason !== null && tools.pressed !== 'rect' && tools.pressed !== 'ellipse'
          }
          polygonDisabled={tools.polygonReason !== null && tools.pressed !== 'polygon'}
          rectTitle={tools.rectReason ?? undefined}
          polygonTitle={tools.polygonReason ?? undefined}
          notes={
            tools.rectReason !== null && tools.polygonReason === null ? [tools.rectReason] : []
          }
          onTool={(tool) => {
            if (tool !== 'select') choose(tool);
          }}
          onPolygon={() => choose('polygon')}
        />
      ) : null}
      {holes.length === 0 ? (
        <p className="text-sm text-muted-foreground">인쇄 제외 영역이 없습니다.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {holes.map((_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: 구멍에는 별도 ID 가 없고 순서가 식별자다.
            <li key={index} className="flex items-center gap-2">
              <Button
                type="button"
                variant={selectedIndex === index ? 'secondary' : 'ghost'}
                aria-pressed={selectedIndex === index}
                onClick={() => onSelect(index)}
              >
                인쇄 제외 영역 {index + 1}
              </Button>
              <DeleteButton
                iconOnly
                label={`인쇄 제외 영역 ${index + 1} 삭제`}
                disabled={disabled}
                onClick={() => onRemove(index)}
              />
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
};
